import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import Select from '@/components/Select';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';

interface ILearningFilterValues {
  school_id: string[];
  parent_id: string[];
}

interface ILearningFilterModalProps {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: ILearningFilterValues) => void;
  initialValues?: Partial<ILearningFilterValues>;
  schoolOptions?: { value: string; label: string }[];
  parentOptions?: { value: string; label: string }[];
}

const LearningFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
  schoolOptions = [],
  parentOptions = [],
}: ILearningFilterModalProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();

  const [schoolId, setSchoolId] = useState<string[]>(
    initialValues.school_id || []
  );

  const [parentId, setParentId] = useState<string[]>(
    initialValues.parent_id || []
  );

  const handleClear = () => {
    setSchoolId([]);
    setParentId([]);
    onApply({ school_id: [], parent_id: [] });
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply({
      school_id: schoolId,
      parent_id: parentId,
    });
    setIsOpen(false);
  };

  const isDisableApply =
    (!Array.isArray(schoolId) || schoolId.length === 0) &&
    (!Array.isArray(parentId) || parentId.length === 0);

  if (!hasAccess(ERoleAccess.REPORTS_ANALYTICS)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled: isDisableApply,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        variant: 'link',
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        <Select
          name={formatText('report-analytics.learning.filters.school')}
          options={schoolOptions}
          selected={schoolId}
          onChange={(value) => setSchoolId(Array.isArray(value) ? value : [])}
          placeholder={formatText('common.select')}
          isMulti={true}
          isClearable={true}
          className="min-h-13"
        />

        <Select
          name={formatText('report-analytics.learning.filters.parents')}
          options={parentOptions}
          selected={parentId}
          onChange={(value) => setParentId(Array.isArray(value) ? value : [])}
          placeholder={formatText('common.select')}
          isMulti={true}
          isClearable={true}
          className="min-h-13"
        />
      </div>
    </CustomModal>
  );
};

export default LearningFilterModal;
